import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import { actionCardApi as cardApi } from '~/pages/CardSaga/ActionCard/actionCard.api';
import togglePublishedState from '~/api/togglePublishedState.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchActionCardApi = createAsyncThunk(
    'cardSaga/actionCard/fetchActionCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await cardApi.getAll();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue([]); //`throw` will dispatch RejectedAction. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        }
    },
);

export const toggleActionCardPublishedStateApi = createAsyncThunk(
    'cardSaga/actionCard/toggleActionCardPublishedStateApi',
    async (id, { dispatch, rejectWithValue }) => {
        try {
            await togglePublishedState(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch);
            throw rejectWithValue([]); //`throw` will dispatch RejectedAction. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        }
    },
);

export const createActionCardApi = createAsyncThunk(
    'cardSaga/actionCard/createActionCardApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await cardApi.create();
            dispatch(requestFinished());

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            throw rejectWithValue();
        }
    },
);

export const storeActionCardApi = createAsyncThunk(
    'cardSaga/actionCard/storeActionCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;
        try {
            dispatch(requestStarted());
            await cardApi.store(values);

            alertSwal('success', 'Card generated successfully!');
            dispatch(requestFinished());
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue(); //`return` will dispatch `RejectedWithValueAction`. Ref: https://redux-toolkit.js.org/api/createAsyncThunk#promise-lifecycle-actions
        }
    },
);

export const showActionCardApi = createAsyncThunk(
    'cardSaga/actionCard/showActionCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const { data } = await cardApi.show(id);
            dispatch(requestFinished());

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        }
    },
);

export const updateActionCardApi = createAsyncThunk(
    'cardSaga/actionCard/updateActionCardApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, id, values } = params;
        try {
            dispatch(requestStarted());
            await cardApi.update(id, values);
            alertSwal('success', 'Card updated successfully!');
            dispatch(requestFinished());

            return values;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        }
    },
);

export const deleteActionCardItemApi = createAsyncThunk(
    'cardSaga/actionCard/deleteActionCardItemApi',
    async (params, { dispatch }) => {
        const { navigate, id } = params;

        try {
            await cardApi.delete(id);

            return id;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);
        }
    },
);

export const actionCardSlice = createSlice({
    name: 'cardSaga/actionCard',
    initialState: { rows: [], emptyModel: {}, editedModel: {}, frontSides: [], backSides: [], allPictures: [] },
    reducers: {
        clearEditedActionCard(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchActionCardApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchActionCardApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(toggleActionCardPublishedStateApi.fulfilled, (state, { payload }) => {
                const updateRow = state.rows.find((row) => row.id === payload);
                updateRow.is_ready = !updateRow.is_ready;
            })
            .addCase(toggleActionCardPublishedStateApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(createActionCardApi.fulfilled, (state, { payload }) => {
                state.emptyModel = payload.model;
                state.frontSides = payload.frontSides;
                state.backSides = payload.backSides;
                state.allPictures = payload.allPictures;
            })
            .addCase(createActionCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(storeActionCardApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeActionCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showActionCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload.model;
                state.frontSides = payload.frontSides;
                state.backSides = payload.backSides;
                state.allPictures = payload.allPictures;
            })
            .addCase(showActionCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(updateActionCardApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make ActionUpdate re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.id);
                updatedRow.name = payload.name;
            })
            .addCase(updateActionCardApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder.addCase(deleteActionCardItemApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedActionCard } = actionCardSlice.actions;
